import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { interval } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { Tpl3plService } from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { RawResponseModalComponent } from '../shared/raw-response-modal/raw-response-modal.component';

@Component({
	selector: 'app-tpl3pl-product-master',
	templateUrl: './tpl3pl-product-master.component.html',
	styleUrls: ['./tpl3pl-product-master.component.scss']
})
export class Tpl3plProductMasterComponent implements OnInit, OnDestroy {
	loading = false;
	catalogItems = 0;
	skuPushing = false;
	skuProgress = 0;
	skuTotal = 0;
	skuCreated = 0;
	skuUpdated = 0;
	skuDeleted = 0;
	skuFailed = 0;
	skuErrors: Array<{ stockid: string; message: string }> = [];
	skuDone = false;
	skuCancelled = false;
	skuPhase: 'sync' | 'remove' = 'sync';
	private skuPollSub: Subscription | null = null;
	private configSavedSub: Subscription | null = null;

	constructor(
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.loadCatalogCount();
		this.loadSkuSyncStatus();
		this.configSavedSub = this.tpl3plService.configSaved$.subscribe(() => this.loadCatalogCount());
	}

	ngOnDestroy(): void {
		this.configSavedSub?.unsubscribe();
		this.skuPollSub?.unsubscribe();
	}

	loadCatalogCount(): void {
		this.loading = true;
		this.tpl3plService.getInventoryStatus(undefined).subscribe({
			next: (result) => {
				this.catalogItems = result?.catalog_items ?? 0;
				this.loading = false;
			},
			error: () => { this.loading = false; }
		});
	}

	loadSkuSyncStatus(): void {
		this.tpl3plService.getSkuSyncStatus().subscribe({
			next: (res) => {
				if (!res.success || !res.job) { return; }
				const j = res.job;
				this.skuTotal = j.total;
				this.skuProgress = j.current_offset;
				this.skuCreated = j.created;
				this.skuUpdated = j.updated;
				this.skuDeleted = j.deleted;
				this.skuFailed = j.failed;
				this.skuErrors = j.errors || [];
				this.skuPhase = (j.phase === 'remove' ? 'remove' : 'sync');
				this.skuPushing = j.status === 'pending' || j.status === 'running';
				this.skuDone = j.status === 'completed' || j.status === 'failed' || j.status === 'cancelled';
				this.skuCancelled = j.status === 'cancelled';
			},
			error: () => {}
		});
	}

	pushSkusToPgs(): void {
		this.skuCancelled = false;
		this.skuPushing = true;
		this.skuProgress = 0;
		this.skuTotal = 0;
		this.skuCreated = 0;
		this.skuUpdated = 0;
		this.skuDeleted = 0;
		this.skuFailed = 0;
		this.skuErrors = [];
		this.skuDone = false;
		this.tpl3plService.startSkuSync(false).subscribe({
			next: (startRes) => {
				if (!startRes.success) {
					this.skuPushing = false;
					this.globalSearchService.showNotification(startRes.message || 'Failed to start', 'danger', 'bottom', 'right');
					return;
				}
				this.skuPollSub = interval(2500).pipe(
					switchMap(() => this.tpl3plService.getSkuSyncStatus()),
					takeWhile((res) => res.success && res.job && (res.job.status === 'pending' || res.job.status === 'running'), true)
				).subscribe({
					next: (res) => {
						if (!res.job) { return; }
						const j = res.job;
						this.skuTotal = j.total;
						this.skuProgress = j.current_offset;
						this.skuCreated = j.created;
						this.skuUpdated = j.updated;
						this.skuDeleted = j.deleted;
						this.skuFailed = j.failed;
						this.skuErrors = j.errors || [];
						this.skuPhase = (j.phase === 'remove' ? 'remove' : 'sync');
						if (j.status === 'completed' || j.status === 'failed' || j.status === 'cancelled') {
							this.skuPushing = false;
							this.skuDone = true;
							this.skuCancelled = j.status === 'cancelled';
							const parts = [];
							if (this.skuCreated > 0) { parts.push(this.skuCreated + ' created'); }
							if (this.skuUpdated > 0) { parts.push(this.skuUpdated + ' updated'); }
							if (this.skuDeleted > 0) { parts.push(this.skuDeleted + ' removed'); }
							if (this.skuFailed > 0) { parts.push(this.skuFailed + ' failed'); }
							this.globalSearchService.showNotification(
								parts.length ? parts.join(', ') : (j.status === 'completed' ? 'Complete' : j.status),
								this.skuFailed > 0 ? 'danger' : 'success', 'bottom', 'right'
							);
						}
					}
				});
			},
			error: (err) => {
				this.skuPushing = false;
				this.globalSearchService.showNotification(err?.error?.message || 'Failed to start', 'danger', 'bottom', 'right');
			}
		});
	}

	cancelSkuPush(): void {
		this.skuPollSub?.unsubscribe();
		this.skuPollSub = null;
		this.skuPushing = false;
		this.skuDone = true;
		this.globalSearchService.showNotification('Stopped polling; job continues on server. Refresh to see status.', 'warning', 'bottom', 'right');
	}

	get skuProgressPercent(): number {
		if (!this.skuTotal) { return 0; }
		return Math.round((this.skuProgress / this.skuTotal) * 100);
	}

	openManualPushRaw(): void {
		this.dialog.open(RawResponseModalComponent, {
			width: '720px',
			maxWidth: '96vw',
			maxHeight: '90vh',
			data: {
				context: 'sku_push',
				stockid: undefined,
				content: {},
				onSent: () => this.loadSkuSyncStatus()
			},
			panelClass: 'raw-response-dialog'
		});
	}
}
